import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import ProjectProfileLogo from "./ProjectProfileLogo.jsx";
import DeleteProjectContent from "./DeleteProjectContent.jsx";

import {ProjectApi, ProjectPermission} from "../../api/authority/generated/index.ts";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import BillingDataForm from "./Billing/BillingDataForm.jsx";
import CardForm from "./Billing/CardForm.jsx";
import Button from "../../components/Button.jsx";
import {Panel} from "../admin/ReportingLab.jsx";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";
import SuperadminIndicator from "../../components/auth/SuperadminIndicator.jsx";
import InvoiceList from "./Billing/InvoiceList.jsx";
import {useHasPermission} from "../../components/auth/rolesAndPermissions.js";
import ProjectMcpGrants, {getProjectSettingsAccess} from "./ThirdPartyServiceIntegrations/ProjectMcpGrants.jsx";

export default function ProjectSettings({project}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {context, getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);
    const isAdministrator = useIsAdministrator();
    const hasPermission = useHasPermission();
    const isProjectMember = context.currentUserProjects.some((candidate) => candidate.id === project.id);
    const {canEditProfile, canViewBilling, canViewMcp} = getProjectSettingsAccess(
        project,
        hasPermission,
        isProjectMember,
    );

    const initialProjectInputState = {
        name: project.name,
        logoUrl: project.logoUrl,
        separateDomainHostNames: project.separateDomainHostNames ?? [],
    };

    const [projectInput, setProjectInput] = useState(initialProjectInputState);
    const [newUri, setNewUri] = useState('');
    const [saveState, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {
        setProjectInput(initialProjectInputState);
    }, [project.id]);

    const handleSaveProject = async () => {
        setDownloading(true);
        try {
            const projectPayload = {...projectInput};
            if (!isAdministrator) {
                delete projectPayload.separateDomainHostNames;
            }

            const response = await new ProjectApi(getAuthorityConfiguration()).updateProject({
                projectId: project.id,
                projectInput: projectPayload,
            });
            setResult(response);
            toast.success(t('saved'));
            resetCurrentUserProjects();
        } catch (e) {
            const errorMessage = await et(e);
            toast.error(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <WideSecureLayout title={t('route.project-settings')}>
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <div className="grid gap-6">
                    {canViewMcp && <Panel title={t('project.mcp.box-title', {defaultValue: 'MCP pro AI asistenty'})} contentClassName="p-5">
                        <ProjectMcpGrants project={project}/>
                    </Panel>}

                    {canEditProfile && <Panel title={t('project.misc-settings')} loading={saveState.downloading} contentClassName="p-5">
                            <ProjectProfileLogo project={project}/>

                            <FormControl
                                className="mt-5"
                                key={'name' + project.id}
                                type={'text'}
                                variant="modern"
                                label={t('project.name')}
                                value={projectInput.name}
                                onChange={(e) => setProjectInput({...projectInput, name: e.target.value})}
                            />
                            {isAdministrator && <div className="relative mt-4 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-500/30 dark:bg-violet-500/10">
                                <SuperadminIndicator className="absolute right-3 top-3"/>
                                <div className="pr-10">
                                    <FormControl
                                        key={'separateDomainHostNames' + project.id}
                                        type={'text'}
                                        variant="modern"
                                        label={t('project.separate-domain-host-names')}
                                        value={projectInput.separateDomainHostNames.join(', ')}
                                        onChange={(e) => setProjectInput({
                                            ...projectInput,
                                            separateDomainHostNames: e.target.value.split(',').map((hostName) => hostName.trim()).filter(Boolean),
                                        })}
                                    />
                                    <p className={'mt-1 text-sm text-slate-500 dark:text-slate-400'}>
                                        {t('project.separate-domain-host-names-description')}
                                    </p>
                                </div>
                            </div>}
                            <div className="mt-5 flex justify-end">
                                <Button size="normal" onClick={handleSaveProject}>
                                    {t('save')}
                                </Button>
                            </div>
                    </Panel>}

                    {canViewBilling && <Panel title={t('project.payment-data')} contentClassName="p-5">
                        <CardForm project={project}/>
                    </Panel>}

                    {canViewBilling && <Panel title={t('billing.invoices.title')} contentClassName="p-5">
                        <InvoiceList key={project.id} project={project}/>
                    </Panel>}

                    {canEditProfile && <Panel title={t('project.delete')} className="hidden border-red-200 xl:block dark:border-red-900/60" contentClassName="p-5">
                        <DeleteProjectContent project={project}/>
                    </Panel>}
                </div>

                {canViewBilling && <Panel title={t('project.billing-data')} contentClassName="p-5">
                    <BillingDataForm project={project}/>
                </Panel>}

                {canEditProfile && <Panel title={t('project.delete')} className="border-red-200 xl:hidden dark:border-red-900/60" contentClassName="p-5">
                    <DeleteProjectContent project={project}/>
                </Panel>}
            </div>
        </WideSecureLayout>
    );
}
